import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import CSSTransition from 'react-transition-group/CSSTransition';
import { Button, Typography } from '@which/seatbelt';
import { ControlledDropdown, ControlledTextField, OnwardJourney, Results, ToolBody, ToolFrame, ToolHeading } from '../../components';
import { ResultsCard } from '../../components/ResultsCard/ResultsCard';
import { useTrackedButton, useTrackedInput, useTrackedSelect, useTracking } from '../../hooks';
import { fetchPostcodeData } from '../../shared/utils';
import styles from './BankClosureTool.module.scss';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const BankClosureTool = _ref => {
  let {
    onwardJourneys,
    xmlData
  } = _ref;
  const [bankClosureData, setBankData] = useState(null);
  const [banksList, setBanksList] = useState(defaultBanksList);
  const [hasLoadedData, setHasLoadedData] = useState(false);
  const [bankClosureFormData, setBankClosureFormData] = useState({
    ...defaultFormValues
  });
  const [matchingResults, setMatchingResults] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const resultsRef = useRef(null);
  const {
    control,
    handleSubmit,
    setError,
    formState: {
      errors
    }
  } = useForm({
    defaultValues: defaultFormValues
  });
  const trackingState = useTracking({
    name: 'Bank branch closures',
    category: 'Money'
  });
  const trackedInputProps = useTrackedInput({
    trackingState
  });
  const trackedSelectProps = useTrackedSelect({
    trackingState
  });
  const trackedSubmitButtonProps = useTrackedButton({
    trackingName: 'calculate',
    trackingState
  });
  useEffect(() => {
    setBankData(xmlData);
    setBanksList(fetchUniqueBanks(xmlData));
    setHasLoadedData(true);
  }, [xmlData]);
  const lookupBankClosures = async data => {
    if (hasLoadedData && bankClosureData) {
      const constituencyResponse = await fetchPostcodeData({
        postcode: data.postcode,
        responseKey: 'parliamentary_constituency'
      });
      if (!constituencyResponse.success) {
        setError('postcode', {
          type: 'focus',
          message: constituencyResponse.data
        }, {
          shouldFocus: true
        });
        setShowResults(false);
      } else {
        const constituency = constituencyResponse.data;
        setBankClosureFormData(prev => ({
          ...prev,
          constituency
        }));
        const matches = bankClosureData.filter(closure => {
          return closure.localAuthority.toLowerCase() === constituency.toLowerCase() && (closure.bank.toLowerCase() === data.bank.toLowerCase() || data.bank.toLowerCase() === 'bank');
        });
        if (matches.length > 0) {
          setMatchingResults(matches);
        } else {
          setMatchingResults(null);
        }
        setShowResults(true);
      }
    }
  };
  const onSubmit = async data => {
    setBankClosureFormData(data);
    await lookupBankClosures(data);
  };
  const onInvalidSubmit = () => {
    setShowResults(false);
  };
  useEffect(() => {
    if (showResults) {
      resultsRef.current?.focus();
    }
  }, [showResults, matchingResults]);
  if (hasLoadedData) {
    return /*#__PURE__*/_jsxs(ToolFrame, {
      children: [/*#__PURE__*/_jsxs(ToolBody, {
        "aria-labelledby": "bank-closures-heading",
        children: [/*#__PURE__*/_jsx(ToolHeading, {
          id: "bank-closures-heading",
          children: "Bank branch closures"
        }), /*#__PURE__*/_jsx(Typography, {
          textStyle: "sb-text-body-default-regular",
          children: "Please enter your bank and postcode information below to see if any branches are closing in your area - data covers closures from 2020 and is updated regularly."
        }), /*#__PURE__*/_jsxs("form", {
          name: "Bank closure tool",
          onSubmit: handleSubmit(onSubmit, onInvalidSubmit),
          children: [/*#__PURE__*/_jsx(ControlledDropdown, {
            control: control,
            label: "Bank",
            name: "bank",
            options: banksList,
            ...trackedSelectProps
          }), /*#__PURE__*/_jsx(ControlledTextField, {
            className: styles.formControlledTextField,
            control: control,
            errorMsg: errors.postcode?.message || 'Please enter a valid postcode',
            label: "Your postcode",
            name: "postcode",
            ...trackedInputProps
          }), /*#__PURE__*/_jsx(Button, {
            type: "submit",
            ...trackedSubmitButtonProps,
            children: "Check for closures"
          })]
        })]
      }), /*#__PURE__*/_jsx(CSSTransition, {
        classNames: "results",
        in: showResults,
        timeout: 500,
        children: /*#__PURE__*/_jsx(_Fragment, {
          children: showResults && /*#__PURE__*/_jsxs("div", {
            className: styles.results,
            children: [/*#__PURE__*/_jsxs(Results, {
              "aria-label": "Bank closure results",
              "data-testid": "results",
              ref: resultsRef,
              tabIndex: -1,
              children: [/*#__PURE__*/_jsx(Typography, {
                className: styles.resultsHeader,
                tag: "h3",
                textStyle: "sb-text-heading-small",
                children: "Branch closure details"
              }), matchingResults && /*#__PURE__*/_jsx("ul", {
                className: styles.resultsList,
                children: matchingResults.map(result => /*#__PURE__*/_jsx("li", {
                  children: /*#__PURE__*/_jsx(ResultsCard, {
                    details: {
                      'Branch name': result.branch,
                      'Closing date': result.closingDate,
                      Postcode: result.postcode,
                      'Parliamentary constituency': result.localAuthority
                    },
                    title: result.bank
                  })
                }, `${result.bank}-${result.postcode}`))
              }), !matchingResults && /*#__PURE__*/_jsxs(Typography, {
                textStyle: "sb-text-body-default-regular",
                children: ["There are no ", bankClosureFormData.bank, " branches announced to close in", ' ', bankClosureFormData.constituency, " in ", new Date().getFullYear()]
              })]
            }), /*#__PURE__*/_jsx(OnwardJourney, {
              "aria-label": "Banking advice",
              itemGroup: "Money",
              onwardJourneys: onwardJourneys,
              toolName: "Bank closure tool"
            })]
          })
        })
      })]
    });
  }
  return null;
};
export default BankClosureTool;
///////// IMPLEMENTATION /////////
const defaultFormValues = {
  bank: 'bank',
  postcode: ''
};
const defaultBanksList = [{
  label: 'All banks',
  value: 'bank'
}];
const fetchUniqueBanks = bankClosureData => {
  const uniqueBanks = [...new Set(bankClosureData.map(item => item.bank))].map(bankName => ({
    label: bankName,
    value: bankName
  })).sort((a, b) => a.label.localeCompare(b.label));
  return [...defaultBanksList, ...uniqueBanks];
};